import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { 
  Eye, MousePointer, Users, ShoppingCart, DollarSign,
  TrendingUp, TrendingDown, Sparkles, ArrowRight,
  Instagram, Facebook, Linkedin, Youtube, Twitter,
  Calendar, BarChart3, Activity, Clock, ChevronDown
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

// Sample data for charts
const campaignData = [
  { name: 'May 10', impressions: 1800000, clicks: 35000 },
  { name: 'May 11', impressions: 2100000, clicks: 42000 },
  { name: 'May 12', impressions: 1950000, clicks: 38000 },
  { name: 'May 13', impressions: 2400000, clicks: 48000 },
  { name: 'May 14', impressions: 2200000, clicks: 44000 },
  { name: 'May 15', impressions: 2600000, clicks: 52000 },
  { name: 'May 16', impressions: 2350000, clicks: 47000 },
];

const platformData = [
  { name: 'Facebook', value: 35.6, color: '#1877F2' },
  { name: 'Instagram', value: 25.6, color: '#E4405F' },
  { name: 'LinkedIn', value: 15.2, color: '#0A66C2' },
  { name: 'YouTube', value: 10.1, color: '#FF0000' },
  { name: 'TikTok', value: 8.7, color: '#000000' },
  { name: 'Others', value: 4.8, color: '#666666' },
];

const funnelData = [
  { stage: 'Visitors', value: 125400, color: '#34d399' },
  { stage: 'Leads', value: 18750, color: '#f472b6' },
  { stage: 'Qualified', value: 2450, color: '#60a5fa' },
  { stage: 'Converted', value: 1380, color: '#E36559' },

];

const socialGrowth = [
  { platform: 'Instagram', icon: Instagram, followers: '124.5K', growth: '+12.4%', color: 'text-pink-500' },
  { platform: 'Facebook', icon: Facebook, followers: '89.2K', growth: '+8.7%', color: 'text-blue-500' },
  { platform: 'LinkedIn', icon: Linkedin, followers: '45.8K', growth: '+15.2%', color: 'text-blue-600' },
  { platform: 'TikTok', icon: Twitter, followers: '67.3K', growth: '+22.1%', color: 'text-zinc-400' },
];

const recentActivity = [
  { action: 'New campaign "Summer Sale" created', time: '2m ago', type: 'campaign' },
  { action: 'AI generated 15 new ad creatives', time: '15m ago', type: 'ai' },
  { action: 'Instagram post scheduled for 7 PM', time: '32m ago', type: 'social' },
  { action: '45 new leads captured from landing page', time: '1h ago', type: 'leads' },
  { action: 'Facebook campaign budget optimized', time: '2h ago', type: 'ai' },
];

const aiInsights = [
  'Your Instagram engagement is 35% higher compared to last week. Consider posting more Reels.',
  'Video content is driving 2.4x more engagement than static images.',
  'Consider increasing budget for Facebook Ads - ROI is 3.2x above average.',
  'Best posting time for your audience: 7 PM - 9 PM on weekdays.',
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('last7days');
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
  ];
  
  const getDateRangeLabel = () => {
    const option = dateRangeOptions.find(o => o.value === dateRange);
    return option ? option.label : 'Select Date Range';
  };

  const metrics = [
    { 
      label: 'Total Impressions', 
      value: '12.42M', 
      change: '+18.6%', 
      positive: true,
      icon: Eye,
      color: 'text-primary',
      link: '/analytics'
    },
    { 
      label: 'Total Clicks', 
      value: '245.8K', 
      change: '+25.4%', 
      positive: true,
      icon: MousePointer,
      color: 'text-emerald-400',
      link: '/analytics'
    },
    { 
      label: 'Leads Generated', 
      value: '18.75K', 
      change: '+31.5%', 
      positive: true,
      icon: Users,
      color: 'text-primary',
      link: '/leads'
    },
    { 
      label: 'Conversions', 
      value: '2.34K', 
      change: '+21.7%', 
      positive: true,
      icon: ShoppingCart,
      color: 'text-emerald-400',
      link: '/campaigns'
    },
    { 
      label: 'Revenue', 
      value: '$78.34K', 
      change: '+27.3%', 
      positive: true,
      icon: DollarSign,
      color: 'text-primary',
      link: '/analytics'
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Welcome back, {user?.name?.split(' ')[0] || 'User'}!</h1>
          <p className="text-muted-foreground">Here's what's happening with your marketing campaigns</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date Range Picker */}
          <div className="relative">
            <button 
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center gap-2 px-4 py-2 bg-secondary rounded-lg border border-border hover:border-primary/50 transition-colors"
              data-testid="date-picker-btn"
            >
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">{getDateRangeLabel()}</span>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
            {showDatePicker && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-lg shadow-lg z-50 py-2">
                {dateRangeOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => { setDateRange(option.value); setShowDatePicker(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-secondary transition-colors ${
                      dateRange === option.value ? 'text-primary bg-primary/10' : ''
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button 
            onClick={() => navigate('/campaigns')}
            className="marketing-btn-primary flex items-center gap-2"
            data-testid="create-campaign-btn"
          >
            <span>+ Create Campaign</span>
          </button>
        </div>
      </div>

      {/* Metrics Grid - Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {metrics.map((metric, index) => (
          <div 
            key={index} 
            onClick={() => navigate(metric.link)}
            className="metric-card cursor-pointer hover:border-primary/50 transition-all"
            data-testid={`metric-${metric.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg bg-secondary flex items-center justify-center ${metric.color}`}>
                <metric.icon className="w-5 h-5" />
              </div>
              <div className={`flex items-center gap-1 text-xs font-medium ${metric.positive ? 'text-emerald-400' : 'text-red-400'}`}>
                {metric.positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {metric.change}
              </div>
            </div>
            <p className="text-2xl font-bold">{metric.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{metric.label}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Campaign Performance Chart */}
        <div className="lg:col-span-2 marketing-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold">Campaign Performance</h3>
              <p className="text-sm text-muted-foreground">Impressions and clicks over time</p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary"></div>
                <span className="text-muted-foreground">Impressions</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                <span className="text-muted-foreground">Clicks</span>
              </div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={campaignData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(30 10% 20%)" />
                <XAxis dataKey="name" stroke="hsl(40 15% 55%)" fontSize={12} />
                <YAxis stroke="hsl(40 15% 55%)" fontSize={12} tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'hsl(30 12% 12%)', 
                    border: '1px solid hsl(30 10% 20%)',
                    borderRadius: '8px',
                    color: 'hsl(40 20% 90%)'
                  }} 
                />
                <Line type="monotone" dataKey="impressions" stroke="#E36559" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="clicks" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Performing Platforms */}
        <div className="marketing-card">
          <h3 className="font-semibold mb-4">Top Performing Platforms</h3>
          <div className="space-y-3">
            {platformData.map((platform, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: platform.color }}></div>
                  <span className="text-sm">{platform.name}</span>
                </div>
                <span className="text-sm font-medium">{platform.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Second Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* AI Insights */}
        <div className="marketing-card">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">AI Insights</h3>
          </div>
          <div className="space-y-3">
            {aiInsights.map((insight, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-secondary/50 rounded-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0"></div>
                <p className="text-sm text-muted-foreground">{insight}</p>
              </div>
            ))}
          </div>
          <button className="flex items-center gap-2 text-primary text-sm mt-4 hover:underline">
            View All Insights <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Lead Generation Funnel */}
        <div className="marketing-card">
          <h3 className="font-semibold mb-4">Lead Generation Funnel</h3>
          <div className="space-y-3">
            {funnelData.map((item, index) => {
              const width = (item.value / funnelData[0].value) * 100;
              return (
                <div key={index}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>{item.stage}</span>
                    <span className="font-medium">{item.value.toLocaleString()}</span>
                  </div>
                  <div className="h-8 bg-secondary rounded-lg overflow-hidden">
                    <div 
                      className="h-full rounded-lg transition-all duration-500"
                      style={{ 
                        width: `${width}%`,
                        backgroundColor: item.color
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Social Media Growth */}
        <div className="marketing-card">
          <h3 className="font-semibold mb-4">Social Media Growth</h3>
          <div className="space-y-4">
            {socialGrowth.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                  <div>
                    <p className="text-sm font-medium">{item.platform}</p>
                    <p className="text-xs text-muted-foreground">{item.followers} followers</p>
                  </div>
                </div>
                <span className="text-emerald-400 text-sm font-medium">{item.growth}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="marketing-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Recent Activity</h3>
          </div>
          <button className="text-primary text-sm hover:underline">View All</button>
        </div>
        <div className="space-y-3">
          {recentActivity.map((item, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary"></div>
                <span className="text-sm">{item.action}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span className="text-xs">{item.time}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
